/**
 * The task-shaped transport seam (spec-web-ui §3): components speak TOOL
 * grammar (listClients / searchArtifacts / getDownloadLink…), never HTTP.
 * One interface, several adapters — rest.ts today; mcp-app.ts (widget epic)
 * and sketchup.ts (plugin brief) later reuse the same components untouched.
 *
 * WIRE TYPES are declared HERE, locally, matching the REST serializers
 * byte-for-byte — `import type` from src/ is deliberately avoided so no
 * server-side module can ever be pulled toward the browser bundle, and so
 * this package typechecks standalone under ui/tsconfig.json. Each type
 * cites the serializer that owns the shape; if a serializer gains a field,
 * add it here too (the transport integration test asserts the shapes over
 * real TCP, so drift fails the suite, not the user).
 */

/** One GET /clients row — src/routes/clients.ts:44-51 (serializeClient). */
export interface ClientRow {
  id: string;
  name: string;
  email: string | null;
  createdAt: string;
}

/** One GET /clients/:id/projects row — src/routes/clients.ts:61-68 (serializeProject). */
export interface ProjectRow {
  id: string;
  clientId: string;
  name: string;
  createdAt: string;
}

/**
 * The check-out lock triple (spec-artifact-locking §5/§7), carried by every
 * artifact row — src/routes/artifacts.ts serializeArtifactLock. The keys are
 * ABSENT when the artifact is free (that omission IS the wire signal), so
 * they are optional here; `lockNote` is present-but-null for a lock taken
 * without a reason. Read them through ui/src/lib/locking.ts, never raw.
 */
export interface ArtifactLockFields {
  lockedBy?: string;
  lockedAt?: string;
  lockNote?: string | null;
}

/** Base artifact fields — src/routes/artifacts.ts:55-64 (serializeArtifact). */
export interface ArtifactRow extends ArtifactLockFields {
  id: string;
  projectId: string;
  name: string;
  type: string;
  currentVersionId: string | null;
  createdAt: string;
}

/**
 * One GET /projects/:id/artifacts row — src/routes/artifacts.ts:105-115
 * (serializeArtifactListRow): base fields + the current-version enrichment.
 */
export interface ArtifactListRow extends ArtifactRow {
  currentVersionNumber: number | null;
  versionCount: number;
  updatedAt: string;
  sizeBytes: number | null;
  currentVersionMessage: string | null;
  currentVersionAuthor: string | null;
}

/**
 * One GET /artifacts/search row — src/routes/artifacts.ts:127-134
 * (serializeArtifactSearchRow): the list row + full address context.
 */
export interface ArtifactSearchRow extends ArtifactListRow {
  clientId: string;
  clientName: string;
  projectName: string;
}

/** One version object — src/routes/artifacts.ts:81-93 (serializeVersion). */
export interface VersionRow {
  id: string;
  artifactId: string;
  versionNumber: number;
  blobHash: string;
  sizeBytes: number;
  parentVersionId: string | null;
  author: string;
  message: string | null;
  createdAt: string;
}

/** GET /artifacts/:id — src/routes/artifacts.ts:224-238 (route handler):
 * base artifact fields + the full version chain (versionNumber ASC —
 * src/domain/artifacts.ts:284 orderBy). */
export interface ArtifactDetail extends ArtifactRow {
  versions: VersionRow[];
}

/**
 * `POST /artifacts/:id/lock` response — src/routes/artifacts.ts
 * serializeLockState: the lock state AFTER the call, with the artifact name
 * riding along so a shell can report "paėmėte Marols.skp" without a
 * follow-up read.
 */
export interface ArtifactLockRow {
  artifactId: string;
  name: string;
  lockedBy: string | null;
  lockedAt: string | null;
  lockNote: string | null;
  /**
   * The version the claim was made against — the base the edit starts from
   * (spec-artifact-locking §8; plugin channel reply 127 item 3). The take
   * flow remembers THIS number rather than the row's, because the claim is
   * the moment the base is decided, and on a shell with no download step
   * (SketchUp — ui/BRIDGE.md §4.3) the lock is the only primitive that fires.
   * `null` = the artifact has no versions yet (nothing to be based on), and
   * OPTIONAL on the type because a vendored bundle may face a pre-1.3.0 host
   * that does not send it — read it defensively, fall back to the row.
   */
  currentVersionNumber?: number | null;
}

/**
 * `DELETE /artifacts/:id/lock` response — the same state (always free
 * afterwards) plus WHO was released. Idempotent: releasing a free artifact
 * answers 200 with `released: false`, which is what makes an abandon safe
 * to repeat and a takeover distinguishable in the audit stream.
 */
export interface ArtifactUnlockRow extends ArtifactLockRow {
  released: boolean;
  previousLockedBy: string | null;
  previousLockedAt: string | null;
  previousLockNote: string | null;
}

/** POST .../download-link response — src/routes/download.ts:51-63
 * (serializeDownloadLink). */
export interface DownloadLink {
  url: string;
  expiresAt: string;
  filename: string;
  sizeBytes: number;
}

/**
 * Per-artifact actions a transport can fulfil (see {@link StorageTransport.caps}
 * `actions`). The component renders EXACTLY the advertised set as
 * row/version buttons and emits ONE typed {@link ArtifactActionIntent} —
 * which buttons exist is pure capability DATA, never host branching (the
 * "one component, many shells" bet — ui-surface.md "Komponentų dalijimasis
 * tarp projektų"). The browser (REST) shell advertises `["download"]`; a
 * future SketchUp adapter advertises `["open", "import"]` WITHOUT any
 * `if (host === …)` in the components.
 *
 * ADDITIVE + capability negotiation: unknown ids arriving from a NEWER
 * host are skipped by the component (a vendored/older bundle must not break
 * when the host learns new tricks — it renders what it understands). Grow
 * this union only by appending; never renumber existing members.
 */
export type ArtifactActionId = "download" | "open" | "import";

/**
 * The single typed intent an action button emits (up to the shell, which
 * fulfils it — the REST shell maps "download" to mint-link + navigate).
 * Omitted `versionNumber` = the artifact's CURRENT version (the adapter
 * resolves it — task grammar, same rule as
 * {@link StorageTransport.getDownloadLink}).
 */
export interface ArtifactActionIntent {
  action: ArtifactActionId;
  artifactId: string;
  versionNumber?: number;
}

/**
 * Options for a long-running action fulfilment (open/import on the
 * SketchUp shell — {@link StorageTransport.performArtifactAction}):
 * `onProgress` receives byte counts (`totalBytes` null = the host does
 * not know the total → indeterminate UI), `signal` cancels the in-flight
 * action (an outcome, surfaced as `action_aborted`). The REST/browser
 * shell has no performArtifactAction, so these are only ever honored by
 * the SketchUp adapter.
 */
export interface ArtifactActionOptions {
  onProgress?: (bytes: number, totalBytes: number | null) => void;
  signal?: AbortSignal;
}

/** What fulfilling an action yields, for the component's status strip. */
export interface ArtifactActionResult {
  /** Host-composed LT completion text (SketchUp loader messages arrive
   * from Ruby already human-ready); when present it wins over the
   * component's per-action default. Presentation never lives in an
   * adapter otherwise — this is the ONE host-worded exception. */
  message?: string;
  /** The file name acted upon, for the component's default status text. */
  filename?: string;
}

/**
 * The active model's facts, answered by `host.getModelInfo`
 * (spec-upload-unification §3/§4; ui/BRIDGE.md §4.7). STATELESS: the host
 * remembers nothing between calls — `upload.perform` always takes the
 * model active AT THAT MOMENT (the single source of truth); this is only
 * the pre-pick snapshot the upload panel shows.
 */
export interface ModelInfo {
  /** The active model's file name, or null when the model has NEVER been
   * saved (the UI refuses with `model_not_saved` — an unsaved model has no
   * name to match create-vs-append against). */
  fileName: string | null;
  /** Size on disk of the LAST save, or null when never saved. When `dirty`
   * this is informational only — the real size exists after the host's
   * save-then-upload; progress uses the real total (BRIDGE.md §4.4). */
  sizeBytes: number | null;
  /** Unsaved changes exist — the host will `model.save` BEFORE uploading;
   * the panel shows the pending note so this never surprises. */
  dirty: boolean;
}

/** uploadFile input (spec-web-ui §2 "Upload"): multipart XHR with progress
 * + abort in the browser, fetch fallback elsewhere. `file.name` drives the
 * server-mirroring type inference AND the create-vs-append name match;
 * author is NEVER passed by the component — the ADAPTER fills the wire's
 * required author field via its `sessionAuthor` seam (rest.ts options),
 * but since B-39 the SERVER is authoritative for cookie sessions: the
 * stored author is derived server-side from the session user, whatever
 * the wire carried. Display {@link UploadResult.author}, never a local
 * guess.
 *
 * `file` is OPTIONAL since the upload unification (spec-upload-unification
 * §3): the SketchUp intent path has no browser `File` — its bytes are
 * Ruby-mediated, the source is the ACTIVE MODEL. The REST adapter still
 * REQUIRES it (absent → `internal_error`, a programming error: the
 * component's "file" source mode always passes one); the SketchUp adapter
 * ignores it either way. */
export interface UploadFileArgs {
  projectId: string;
  file?: File;
  message?: string;
  /**
   * The authoritative RETURN target (spec-artifact-locking §8): the id of
   * the artifact whose lock the caller holds. When present the adapter must
   * NOT name-resolve — no create-vs-append decision, no ambiguity refusal,
   * no `confirmedKind` cross-check: the upload is an APPEND onto THIS
   * artifact and nothing else. An artifact's name may legitimately differ
   * from the returned file's (`dd` vs `dd.skp`), and two artifacts in one
   * project may share a name, so a name match can silently write to the
   * WRONG file and leave the held one still checked out (channel bug report
   * 127d). Absent = an ordinary upload, name-resolved exactly as before.
   *
   * On the SketchUp transport it travels as the `upload.perform`
   * `artifactId` param (ui/BRIDGE.md §4.4, contract 1.4.0) and the HOST
   * skips its own name resolution the same way.
   */
  artifactId?: string;
  /**
   * The version the upload was edited FROM (spec-artifact-locking §4.2):
   * travels as `metadata.baseVersionNumber` on the append path and makes
   * the server refuse (`version_conflict`) if the artifact moved on while
   * the user was editing. Ignored on the create path — a brand-new
   * artifact has no base. The component fills it from its take-for-work
   * memory (ui/src/lib/base-memory.ts); users never see the number.
   */
  baseVersionNumber?: number;
  /**
   * The create-vs-append kind the CALLER already confirmed with the user
   * (ui/src/lib/upload-plan.ts planUpload over the rows it had). The REST
   * adapter re-plans authoritatively at send time; when its answer
   * DISAGREES with this one the world moved between confirm and submit (a
   * colleague created — or removed — a same-named artifact), and the
   * upload is refused with `upload_plan_changed` instead of silently
   * switching paths.
   *
   * EXCEPTION — {@link UploadFileArgs.artifactId}: on a RETURN the target is
   * already decided by id, so there is no create-vs-append answer to
   * disagree with and this field is accepted-and-IGNORED (the kind is
   * `append` by definition). The one combination that cannot be honest,
   * `artifactId` together with `"create"`, is refused as a caller-state
   * inconsistency (`internal_error`, no request sent — rest.ts uploadFile).
   *
   * This is what keeps every append inside the take-for-work discipline
   * (spec-artifact-locking §8): an append the caller planned as a CREATE
   * carries neither the auto-lock claim nor a base version, so sending it
   * would be the one write outside the locking rules. Omitting the field
   * disables the cross-check — machine seats and pre-locking callers keep
   * the old behavior (spec §2 still allows lock-less uploads).
   */
  confirmedKind?: "create" | "append";
  /** Byte-level upload progress (XHR path only — fetch has no upload
   * progress events; callers must treat "no calls" as indeterminate). */
  onProgress?: (sentBytes: number, totalBytes: number) => void;
  signal?: AbortSignal;
}

/** uploadFile result (W3): enough to reselect/refresh the uploaded row. */
export interface UploadResult {
  artifactId: string;
  artifactName: string;
  versionNumber: number;
  /** The STORED author, as the server answered (201 body's version
   * object). B-39: for cookie sessions the server derives it from the
   * session user, overriding whatever the wire carried — UI must show
   * THIS value, never the client-side sessionAuthor guess. */
  author: string;
}

/**
 * Client-side error codes, additive to the server registry
 * (src/domain/errors.ts): `network_error` (fetch itself failed — no
 * response), `not_implemented` (capability absent in this adapter),
 * `upload_aborted` (the caller's AbortSignal fired mid-upload — user
 * pressed "Atšaukti"; an outcome, not a failure), and
 * `artifact_name_ambiguous` (the project already holds >1 artifact with
 * the file's exact name, so create-vs-append is undecidable — mirrors the
 * MCP upload_artifact refusal, mcp/handlers.ts:613-619; MCP-layer code,
 * deliberately NOT in the domain registry), and `upload_plan_changed` (the
 * adapter's authoritative create-vs-append answer disagrees with the kind
 * the caller confirmed — see UploadFileArgs.confirmedKind; no bytes are
 * sent, the user re-picks and the take-for-work discipline applies to the
 * new plan). Additional client-side codes
 * from the SketchUp shell (ui/src/transport/sketchup.ts; see ui/BRIDGE.md):
 * `action_aborted` (an in-flight open/import was cancelled — same outcome
 * semantics as `upload_aborted`), `bridge_unavailable` (window.sketchup
 * absent — the bundle is running outside CEF), `bridge_contract_mismatch`
 * (host and bundle disagree on the UI_CONTRACT_VERSION major, or the host
 * answered a malformed envelope), `disposed` (the transport was torn down
 * — dialog close — with calls still in flight; SketchUpTransport.dispose),
 * and `model_not_saved` (the active model has never been saved, so there
 * is nothing named to upload — raised by the component's getModelInfo
 * pre-check AND by the host's own `upload.perform` reject, a registry-
 * style bridge code; double protection, spec-upload-unification §5).
 * There is NO bridge timeout: a SketchUp call settles only on the host's
 * completion signal or a user abort (ui/BRIDGE.md §3). Everything else on
 * {@link TransportError} is a registry code parsed from the
 * `{ error: { code, message } }` envelope (src/http/errors.ts errorBody).
 * Origin caveat completing the taxonomy: the registry codes
 * `validation_failed` and `internal_error` can ALSO be emitted
 * client-side, so a TransportError carrying them is NOT proof the server
 * was reached — and `status` does not discriminate origin either. The
 * client-side `validation_failed` (no session author to attach, rest.ts
 * uploadFile) carries `status === null`, as does the client-side
 * `internal_error` no-file guard (uploadFile without a `file` on the REST
 * transport — a programming error, no request sent); the OTHER client-side
 * `internal_error` emissions keep a NON-null status (a non-envelope
 * error body degrades with the real HTTP status; a create 201 missing
 * its version passes 201). Treat these two codes as origin-ambiguous.
 */
export type ClientErrorCode =
  | "network_error"
  | "not_implemented"
  | "upload_aborted"
  | "action_aborted"
  | "artifact_name_ambiguous"
  | "upload_plan_changed"
  | "bridge_unavailable"
  | "bridge_contract_mismatch"
  | "disposed"
  | "model_not_saved";

/**
 * The ONE error type transport methods throw. `sessionExpired` is the
 * shell's signal to drop to the login view: any 401 on the transport means
 * the cookie session is missing/expired/revoked (the browser never holds a
 * Bearer key — R23 §2(a)).
 */
export class TransportError extends Error {
  /** Registry code or {@link ClientErrorCode}. */
  readonly code: string;
  /** HTTP status, or null when no response was received. */
  readonly status: number | null;
  /**
   * The error envelope's `details` payload, verbatim and UNTYPED
   * (src/http/errors.ts errorBody carries it only for some codes). The
   * locking refusals are what made it necessary: `lock_held` /
   * `artifact_locked` carry holder + since + note and `version_conflict`
   * carries the current version + author + timestamp — the facts a user
   * needs to decide what to do next (spec-artifact-locking §8 "Conflict
   * message"). Read it defensively through ui/src/lib/locking.ts, which
   * falls back to the generic gloss when the shape is not what it expects.
   */
  readonly details: unknown;

  constructor(
    code: string,
    message: string,
    status: number | null = null,
    details?: unknown,
  ) {
    super(message);
    this.name = "TransportError";
    this.code = code;
    this.status = status;
    this.details = details;
  }

  get sessionExpired(): boolean {
    return this.status === 401;
  }
}

/**
 * The task-shaped storage transport (spec-web-ui §3). Adapters own
 * credentials and the byte path entirely; components only ever see these
 * methods and `caps`. If non-data methods ever grow past openExternal,
 * split a HostShell interface (R23 §1 hygiene) instead of widening this.
 */
export interface StorageTransport {
  /** Capability flags — components HIDE affordances whose cap is false
   * (e.g. no upload UI when `upload` is false) and render EXACTLY the
   * advertised `actions` set (never `if (host === …)` — capability data is
   * the only allowed variance between shells). `upload` may reflect the
   * LIVE session: for REST it requires the `sessionAuthor` seam (an upload
   * capability without an author source would be a lie, W3) AND — when a
   * `sessionScope` seam is wired — a signed-in user whose scope is not
   * `read` (B-39 ride-along: read-scope users never see the upload
   * affordance; the server's domain write boundary `insufficient_scope`
   * remains the enforcement backstop). */
  readonly caps: {
    readonly upload: boolean;
    /** Per-artifact actions this transport fulfils, in RENDER ORDER (first
     * = the primary button). rest.ts advertises `["download"]`; a later
     * SketchUp adapter advertises `["open", "import"]`. The component
     * renders one button per id and emits an {@link ArtifactActionIntent};
     * ids it does not recognise are skipped (capability negotiation). */
    readonly actions: readonly ArtifactActionId[];
  };

  listClients(): Promise<ClientRow[]>;
  listProjects(clientId: string): Promise<ProjectRow[]>;
  listArtifacts(args: { projectId: string; q?: string }): Promise<ArtifactListRow[]>;
  searchArtifacts(q: string): Promise<ArtifactSearchRow[]>;
  getArtifact(id: string): Promise<ArtifactDetail>;
  /** Mint a presigned link for one version; omitted versionNumber = the
   * artifact's CURRENT version (adapter resolves it — task grammar). */
  getDownloadLink(id: string, versionNumber?: number): Promise<DownloadLink>;
  /**
   * Fulfil a NON-download {@link ArtifactActionIntent} (open/import) that
   * this shell advertised via caps.actions. OPTIONAL: a shell whose
   * advertised actions are all self-fulfilled by other methods leaves it
   * unset — the REST/browser shell fulfils its only action ("download")
   * via getDownloadLink + openExternal and does NOT implement this. When a
   * transport advertises an action it cannot route here, the component
   * surfaces a clear error rather than silently no-op'ing it (PR #45
   * review minor 3 — the exhaustive-dispatch guard, ui/src/lib/
   * artifact-actions.ts planArtifactAction). The SketchUp adapter
   * implements it: Ruby downloads the bytes and opens/imports them into
   * the model, streaming progress and honoring `options.signal`. */
  performArtifactAction?(
    intent: ArtifactActionIntent,
    options?: ArtifactActionOptions,
  ): Promise<ArtifactActionResult>;
  /**
   * The active model's facts, for the "activeModel" upload source
   * (spec-upload-unification §3). OPTIONAL — the method's PRESENCE is the
   * capability signal (same pattern as {@link performArtifactAction}): a
   * transport that has it puts the upload panel into "activeModel" idle
   * mode ("Įkelti aktyvų modelį", no file input); a transport without it
   * (REST/browser) stays in "file" mode. Stateless on the host side: the
   * answer is a snapshot, `uploadFile` re-reads the active model itself.
   */
  getModelInfo?(): Promise<ModelInfo>;
  /**
   * Claim the check-out lock — the "Paimti darbui" half (spec-artifact-
   * locking §8). OPTIONAL, and its PRESENCE is the capability signal (same
   * pattern as {@link performArtifactAction} and {@link getModelInfo}):
   * a shell whose transport lacks it renders NO take/return/abandon/
   * takeover affordances at all, and the file rows keep showing lock
   * BADGES (state is plain row data every shell can display). The browser
   * REST adapter implements it unconditionally; the SketchUp adapter binds
   * it AT HANDSHAKE TIME and only when the host answered `locking: true`
   * (ui/BRIDGE.md §4.1, contract 1.3.0) — a bundle vendored into an older
   * plugin must not offer buttons its host cannot fulfil.
   *
   * Idempotent for the holder (refreshes the note); a lock held by someone
   * ELSE throws `lock_held` carrying holder + since + note. It NEVER
   * steals — a takeover is the deliberate two-step unlock-then-lock.
   */
  lockArtifact?(artifactId: string, note?: string): Promise<ArtifactLockRow>;
  /**
   * Release the check-out lock — „Atsisakyti" (own lock) and the first
   * step of „Perimti…" (a colleague's). Idempotent: releasing a free
   * artifact succeeds with `released: false`. Paired with
   * {@link lockArtifact}: a transport offers both or neither.
   */
  unlockArtifact?(artifactId: string): Promise<ArtifactUnlockRow>;
  uploadFile(args: UploadFileArgs): Promise<UploadResult>;
  /** Hand a URL to the host environment (browser: anchor navigation —
   * presigned downloads are navigations, not fetches; CSP connect-src
   * stays 'self'). */
  openExternal(url: string): void;
}
